'use client';

import { useMutation } from '@apollo/client';
import { Button, Grid, TextField, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { gql } from '../../../../generated';
import { useTranslations } from '../../../hooks/use-translations';
import { ModalDialog, ModalDialogProps, ModalProps } from '../Modal';
import { QrCode } from '../QrCode';

import { AppStoreIcon } from './AppStoreIcon';
import { GooglePlayIcon } from './GooglePlayIcon';
import styles from './TwoFaModal.module.scss';

type TwoFaModalProps = Pick<ModalProps, 'open' | 'onClose'> & {
  onSuccess?(): void;
};

type Step = 'install' | 'qr' | 'code' | 'success';

const REQUEST_QR = gql(/* GraphQL */ `
  mutation REQUEST_QR {
    otpRequestActivate {
      qr_code_data
    }
  }
`);

const ACTIVATE_2FA = gql(/* GraphQL */ `
  mutation ACTIVATE_2FA($token: String!) {
    otpActivate(token: $token)
  }
`);

export const TwoFaModal: FC<TwoFaModalProps> = (props) => {
  const { open, onClose, onSuccess } = props;

  const [currentStep, setCurrentStep] = useState<Step>('install');
  const [verificationCode, setVerificationCode] = useState('');
  const [generatingQrCode, setGeneratingQrCode] = useState(false);

  const codeInputRef = useRef<HTMLInputElement | null>(null);

  const t = useTranslations('2faModal');

  const [requestQr, { loading: loadingQrCodeData, data: requestCodeData }] = useMutation(REQUEST_QR, {
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    },
  });
  const [activate2Fa, { loading: completing }] = useMutation(ACTIVATE_2FA, {
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    },
  });

  const qrOperationsActive = loadingQrCodeData || generatingQrCode;
  const qrCodeData = requestCodeData?.otpRequestActivate.qr_code_data;

  const handleVerificationCodeChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setVerificationCode(e.target.value);
  }, []);

  const additionalContent = useMemo(() => {
    if (currentStep === 'install') {
      return (
        <Grid container columns={2} spacing={2}>
          <Grid item md={1} xs={2}>
            <a
              href="https://apps.apple.com/ru/app/google-authenticator/id388497605"
              target="_blank"
              rel="noreferrer noopener"
            >
              <Button variant="dark" sx={{ paddingTop: 0, paddingBottom: 0 }}>
                <AppStoreIcon />
              </Button>
            </a>
          </Grid>

          <Grid item md={1} xs={2}>
            <a
              href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
              target="_blank"
              rel="noreferrer noopener"
            >
              <Button variant="dark" sx={{ paddingTop: 0, paddingBottom: 0 }}>
                <GooglePlayIcon />
              </Button>
            </a>
          </Grid>
        </Grid>
      );
    }

    if (currentStep === 'qr') {
      if (!qrCodeData) {
        return null;
      }

      return <QrCode className={styles.qr} data={qrCodeData} />;
    }

    if (currentStep === 'code') {
      return (
        <TextField
          inputRef={codeInputRef}
          value={verificationCode}
          label={t('steps.code.input.label')}
          placeholder={t('steps.code.input.placeholder')}
          onChange={handleVerificationCodeChange}
        />
      );
    }
  }, [currentStep, handleVerificationCodeChange, qrCodeData, t, verificationCode]);

  const okButton = useMemo<ModalDialogProps['okButton']>(() => {
    if (currentStep === 'install') {
      return {
        text: qrOperationsActive ? t('buttons.loadingQr') : t('buttons.nextStep'),
        disable: qrOperationsActive,
        onClick: requestQr,
      };
    }

    if (currentStep === 'qr') {
      return {
        text: t('buttons.nextStep'),
        onClick: () => setCurrentStep('code'),
      };
    }

    if (currentStep === 'code') {
      return {
        text: completing ? t('buttons.completing') : t('buttons.complete'),
        disable: completing,
        onClick: async () => {
          const { errors } = await activate2Fa({
            variables: {
              token: verificationCode,
            },
          });

          if (!errors) {
            onSuccess?.();

            setCurrentStep('success');
          }
        },
      };
    }

    if (currentStep === 'success') {
      return {
        text: t('buttons.success'),
        onClick: onClose,
      };
    }
  }, [activate2Fa, completing, currentStep, onClose, onSuccess, qrOperationsActive, requestQr, t, verificationCode]);

  useEffect(() => {
    if (!open) {
      setCurrentStep('install');
      setGeneratingQrCode(false);
      setVerificationCode('');
    }
  }, [open]);

  useEffect(() => {
    if (open && currentStep === 'code') {
      codeInputRef.current?.focus();
    }
  }, [currentStep, open]);

  useEffect(() => {
    if (qrCodeData) {
      setCurrentStep('qr');
    }
  }, [qrCodeData]);

  return (
    <ModalDialog
      open={open}
      title={t('title')}
      onClose={onClose}
      styleOptions={{ width: '25%', minWidth: 'min(400px, 90%)', maxWidth: '520px' }}
      okButton={okButton}
      cancelButton
    >
      <div className={styles.root}>
        <Typography className={styles.text} fontWeight="600">
          {t(`steps.${currentStep}.text`)}
        </Typography>

        {additionalContent}
      </div>
    </ModalDialog>
  );
};
